import { useLayoutEffect, useRef } from 'react'

import { useStatefulRef } from '@core/hooks/useStatefulRef'
import { ensureIntersectionObserver } from '@core/polyfills'

export interface UseVisibilityObserverProps<T> {
  onChange: (
    isVisible: boolean,
    target: T,
    entry: IntersectionObserverEntry,
  ) => void
  /**
   * By default, the hook creates its own stateful ref. In some cases, there
   * might be another hook (useResizeObserver) that also creates a ref, and we
   * want to use that one.
   */
  externalRef?: React.RefObject<T | null>
  /**
   * The threshold that determines whether the observed element is visible. By
   * default, it's 0.7, which means that 70% of the element must be visible.
   */
  threshold: number[]
  /**
   * The element whose bounds are used as the viewport for checking visibility.
   * Defaults to the browser viewport (`null`). Pass a scroll container to scope
   * visibility to that container rather than the page.
   */
  root?: Element | null
}

/**
 * Used for detecting if object is within the viewport based on threshold.
 * Visibility change triggers callback `onChange`.
 * @returns Ref to be attached to the element to be observed.
 *
 * @example
 * const ref = useVisibilityObserver({
 *   onChange: useCallback((isVisible, target) => {
 *     if (isVisible) {
 *       // do something
 *     }
 *   }, []),
 * })
 *
 * return <div ref={ref}>Div to be measured</div>
 */
export function useVisibilityObserver<T extends HTMLElement | null>({
  externalRef,
  onChange,
  threshold,
  root,
}: UseVisibilityObserverProps<T>): {
  current: T | null
} {
  // Storing threshold in a ref to make it non-reactive property to avoid re-creating
  // the observer on every render. Otherwise each component would have to memoize threshold array itself.
  const thresholdRef = useRef(threshold)
  const observerRef = useRef<IntersectionObserver | null>(null)
  // Cannot use useRef here because the ref is not updated until the component
  // is re-rendered.
  const localContainerRef = useStatefulRef<T | null>(null)
  const containerRef = externalRef || localContainerRef

  useLayoutEffect(() => {
    if (!containerRef.current) {
      return
    }
    let cancelled = false
    observerRef.current?.disconnect()
    observerRef.current = null

    ensureIntersectionObserver().then((IO) => {
      if (cancelled) {
        return
      }

      const observer = new IO(
        (entries: IntersectionObserverEntry[]) => {
          const entry = entries[0]
          if (containerRef.current) {
            onChange?.(entry.isIntersecting, containerRef.current, entry)
          }
        },
        {
          threshold: thresholdRef.current,
          // `root` is intentionally left off the options object when undefined
          // so the observer falls back to the browser viewport.
          ...(root ? { root } : {}),
        },
      )
      observerRef.current = observer
      if (containerRef.current) {
        observer.observe(containerRef.current)
      }
    })
    return () => {
      cancelled = true
      observerRef.current?.disconnect()
      observerRef.current = null
    }
    // containerRef.current here triggers a rerender since it's a stateful ref,
    // hence it should be a dependency. `root` is included so the observer is
    // recreated once the scroll container element becomes available.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef.current, onChange, thresholdRef, root])

  return containerRef
}
